import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { parsePageSelection } from '../split/splitPdfEngine';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

async function loadSourcePdf(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

async function buildPdfKeepingIndices(file, keepIndices) {
    if (!keepIndices.length) throw new Error('removeAll');
    const source = await loadSourcePdf(file);
    const out = await PDFDocument.create();
    const pages = await out.copyPages(source, keepIndices);
    for (const page of pages) out.addPage(page);
    const bytes = await out.save();
    return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * Remove specific pages (1-based selection text) from a PDF.
 * @param {File} file
 * @param {string} pageInput e.g. "1-3, 5"
 * @param {number} pageCount
 */
export async function removePdfPages(file, pageInput, pageCount) {
    const removeSet = new Set(parsePageSelection(pageInput, pageCount));
    const keepIndices = [];
    for (let i = 0; i < pageCount; i++) {
        if (!removeSet.has(i)) keepIndices.push(i);
    }
    return buildPdfKeepingIndices(file, keepIndices);
}

async function isPageMostlyBlank(page) {
    const viewport = page.getViewport({ scale: 0.35 });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;

    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let light = 0;
    const total = width * height;
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        if (r >= 245 && g >= 245 && b >= 245) light += 1;
    }
    return light / total >= 0.985;
}

/**
 * Detect (but do not remove) mostly-blank pages.
 * @param {File} file
 * @param {(page: number, total: number) => void} [onProgress]
 * @returns {Promise<number[]>} 0-based indices of blank pages
 */
export async function detectBlankPdfPages(file, onProgress) {
    const data = new Uint8Array(await file.arrayBuffer());
    let pdfDoc;
    try {
        pdfDoc = await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }

    const total = pdfDoc.numPages;
    const blankIndices = [];
    for (let i = 1; i <= total; i++) {
        onProgress?.(i, total);
        const page = await pdfDoc.getPage(i);
        if (await isPageMostlyBlank(page)) blankIndices.push(i - 1);
    }
    return blankIndices;
}

/**
 * Detect and remove blank pages from a PDF.
 * @param {File} file
 * @param {(page: number, total: number) => void} [onProgress]
 */
export async function removeBlankPdfPages(file, onProgress) {
    const blankIndices = new Set(await detectBlankPdfPages(file, onProgress));
    const total = (await loadSourcePdf(file)).getPageCount();

    if (!blankIndices.size) throw new Error('noBlank');
    if (blankIndices.size >= total) throw new Error('removeAll');

    const keepIndices = [];
    for (let i = 0; i < total; i++) {
        if (!blankIndices.has(i)) keepIndices.push(i);
    }

    return {
        blob: await buildPdfKeepingIndices(file, keepIndices),
        removedCount: blankIndices.size,
    };
}
